import { useEffect, useState } from "react";
import { Download, Share2, X } from "lucide-react";

const DISMISS_KEY = "aroma_pwa_install_dismissed_at";
const DISMISS_TTL_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iphone|ipad|ipod/i.test(ua) && !/crios|fxios/i.test(ua) === false
    ? true
    : /iphone|ipad|ipod/i.test(ua);
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function InstallPWAButton() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS Safari never fires beforeinstallprompt — show manual hint
    if (isIOS()) setVisible(true);

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (isIOS() && !deferred) {
      setShowIOSHint(true);
      return;
    }
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch (_) {}
    setDeferred(null);
    setVisible(false);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
    setShowIOSHint(false);
  };

  if (!visible) return null;

  return (
    <>
      <div
        data-testid="pwa-install-banner"
        className="fixed bottom-20 lg:bottom-6 right-4 left-4 lg:left-auto z-50 max-w-sm mx-auto lg:mx-0 bg-[#0A0A0A] text-white rounded-xl shadow-2xl border border-white/10 overflow-hidden"
      >
        <div className="p-4 flex items-start gap-3">
          <img
            src="/icon-192.png"
            alt=""
            className="h-11 w-11 rounded-lg flex-shrink-0"
          />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0055FF]">
              Instal Aplikasi
            </div>
            <div className="mt-0.5 text-sm font-semibold leading-tight">
              Pasang AROMA NET di layar utama
            </div>
            <div className="mt-1 text-xs text-white/60 leading-snug">
              Buka lebih cepat, tanpa alamat browser, seperti aplikasi.
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                data-testid="pwa-install-btn"
                onClick={install}
                className="h-8 px-3 rounded-md bg-[#0055FF] hover:bg-[#0044CC] text-white text-xs font-semibold flex items-center gap-1.5 transition-colors"
              >
                {isIOS() && !deferred ? (
                  <>
                    <Share2 className="h-3.5 w-3.5" />
                    Cara Pasang
                  </>
                ) : (
                  <>
                    <Download className="h-3.5 w-3.5" />
                    Tambahkan ke Layar Utama
                  </>
                )}
              </button>
              <button
                type="button"
                data-testid="pwa-dismiss-btn"
                onClick={dismiss}
                className="h-8 px-2 rounded-md text-white/60 hover:text-white text-xs"
              >
                Nanti Saja
              </button>
            </div>
          </div>
          <button
            type="button"
            aria-label="Tutup"
            onClick={dismiss}
            className="text-white/40 hover:text-white/80"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {showIOSHint && (
        <div
          data-testid="pwa-ios-hint"
          className="fixed inset-0 z-[60] bg-black/70 flex items-end justify-center p-4"
          onClick={() => setShowIOSHint(false)}
        >
          <div
            className="bg-white text-[#0A0A0A] rounded-xl max-w-sm w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#0055FF]">
              Panduan Pemasangan · iOS Safari
            </div>
            <div className="mt-1 text-lg font-black tracking-tight font-display">
              Tambahkan ke Layar Utama
            </div>
            <ol className="mt-4 space-y-3 text-sm">
              <li className="flex gap-3">
                <span className="h-6 w-6 rounded-full bg-[#0055FF] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
                <span>Ketuk ikon <b>Bagikan</b> <Share2 className="inline h-4 w-4 text-[#0055FF]" /> di bawah layar Safari</span>
              </li>
              <li className="flex gap-3">
                <span className="h-6 w-6 rounded-full bg-[#0055FF] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
                <span>Gulir ke bawah lalu pilih <b>Tambah ke Layar Utama</b></span>
              </li>
              <li className="flex gap-3">
                <span className="h-6 w-6 rounded-full bg-[#0055FF] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
                <span>Ketuk <b>Tambah</b> — ikon AROMA NET akan tampil di layar utama HP Anda</span>
              </li>
            </ol>
            <button
              type="button"
              onClick={() => setShowIOSHint(false)}
              className="mt-5 w-full h-10 rounded-md bg-[#0A0A0A] text-white font-semibold"
            >
              Mengerti
            </button>
          </div>
        </div>
      )}
    </>
  );
}
